import { RouteConfig } from 'vue-router';
import { PermissionLevel } from '@/utils/permission';
import '@/styles/index.scss';
import { usersRoutes } from '@/router/users';
import { companyStructureRoutes } from '@/router/companyStructure';
import { productionRoutes } from '@/router/production';
import { transportRoutes } from '@/router/transport';
import { invoicingRoutes } from '@/router/invoicing';
import { pricingRoutes } from '@/router/pricing';

const LazyLayout = () => import('@/layout/index.vue');

export default [
  {
    children: [
      {
        children: [
          {
            component: () => import('@/pages/application/appSettings/index.vue'),
            meta: {
              permissions: [{ AppSetting: PermissionLevel.Read }],
              title: 'menu_admin_appsettings'
            },
            name: 'AppSettings',
            path: 'appsettings'
          },
          {
            component: () => import('@/pages/application/bans/index.vue'),
            meta: {
              permissions: [{ Ban: PermissionLevel.Read }],
              title: 'menu_admin_bans'
            },
            name: 'Bans',
            path: 'bans'
          },
          {
            component: () => import('@/pages/application/penalties/index.vue'),
            meta: {
              permissions: [{ Ban: PermissionLevel.Read }],
              title: 'menu_admin_penalties'
            },
            name: 'Penalties',
            path: 'penalties'
          },
          {
            component: () => import('@/pages/application/countries/index.vue'),
            meta: {
              permissions: [{ Country: PermissionLevel.Read }],
              title: 'menu_admin_countries'
            },
            name: 'Countries',
            path: 'countries'
          },
          {
            component: () => import('@/pages/application/countries/create.vue'),
            hidden: true,
            meta: {
              icon: 'edit',
              permissions: [{ Country: PermissionLevel.Write }],
              title: 'createCountry'
            },
            name: 'CreateCountry',
            path: 'countries/create'
          },
          {
            component: () => import('@/pages/application/countries/edit.vue'),
            hidden: true,
            meta: {
              noCache: true,
              permissions: [{ Country: PermissionLevel.Read }],
              title: 'editCountry'
            },
            name: 'EditCountry',
            path: 'countries/edit/:id'
          },
          {
            component: () => import('@/pages/application/languages/index.vue'),
            meta: {
              permissions: [{ Language: PermissionLevel.Read }],
              title: 'menu_admin_languages'
            },
            name: 'Languages',
            path: 'languages'
          },
          {
            component: () => import('@/pages/application/languages/create.vue'),
            hidden: true,
            meta: {
              icon: 'edit',
              permissions: [{ Language: PermissionLevel.Write }],
              title: 'createLanguage'
            },
            name: 'CreateLanguage',
            path: 'languages/create'
          },
          {
            component: () => import('@/pages/application/languages/edit.vue'),
            hidden: true,
            meta: {
              noCache: true,
              permissions: [{ Language: PermissionLevel.Read }],
              title: 'editLanguage'
            },
            name: 'EditLanguage',
            path: 'languages/edit/:id'
          },
          {
            component: () => import('@/pages/application/translations/index.vue'),
            meta: {
              permissions: [{ Translation: PermissionLevel.Read }],
              title: 'menu_admin_translations'
            },
            name: 'Translations',
            path: 'translations'
          },
          {
            component: () => import('@/pages/application/translations/edit.vue'),
            hidden: true,
            meta: {
              noCache: true,
              permissions: [{ Translation: PermissionLevel.Read }],
              title: 'editTranslation'
            },
            name: 'EditTranslation',
            path: 'translations/edit/:id'
          },
          {
            component: () => import('@/pages/application/configuration/index.vue'),
            meta: {
              permissions: [{ Configuration: PermissionLevel.Read }],
              title: 'menu_admin_configuration'
            },
            name: 'Configuration',
            path: 'configuration'
          },
          {
            component: () => import('@/pages/application/configuration/edit.vue'),
            hidden: true,
            meta: {
              noCache: true,
              permissions: [{ Configuration: PermissionLevel.Read }],
              title: 'editConfiguration'
            },
            name: 'EditConfiguration',
            path: 'configuration/edit/:id'
          },
          {
            component: () => import('@/pages/masterdata/units/index.vue'),
            meta: {
              permissions: [{ Unit: PermissionLevel.Read }],
              title: 'menu_admin_units'
            },
            name: 'Units',
            path: 'units'
          },
          {
            component: () => import('@/pages/masterdata/units/create.vue'),
            hidden: true,
            meta: {
              icon: 'edit',
              permissions: [{ Unit: PermissionLevel.Write }],
              title: 'createUnit'
            },
            name: 'CreateUnit',
            path: 'units/create'
          },
          {
            component: () => import('@/pages/masterdata/units/edit.vue'),
            hidden: true,
            meta: {
              noCache: true,
              permissions: [{ Unit: PermissionLevel.Read }],
              title: 'editUnit'
            },
            name: 'EditUnit',
            path: 'units/edit/:id'
          },
          {
            component: () => import('@/pages/application/accesslog/index.vue'),
            meta: {
              permissions: [{ AccessLog: PermissionLevel.Read }],
              title: 'menu_admin_accesslog'
            },
            name: 'Access log',
            path: 'accesslog'
          },
          {
            component: () => import('@/pages/application/eventlog/index.vue'),
            meta: {
              permissions: [{ EventLog: PermissionLevel.Read }],
              title: 'menu_admin_eventlog'
            },
            name: 'Event Log',
            path: 'eventlog'
          }
        ],
        component: () => import('@/layout/components/Sidebar/SidebarSubMenu.vue'),
        meta: { icon: 'star', title: 'menu_admin_application' },
        name: 'Application',
        path: '/application'
      },
      {
        path: '/users',
        name: 'AdminUserManagement',
        meta: { title: 'menu_admin_users', icon: 'user' },
        component: () => import('@/layout/components/Sidebar/SidebarSubMenu.vue'),
        children: [...usersRoutes]
      } as RouteConfig,
      {
        path: '/companystructure',
        name: 'AdminCompanyStructure',
        meta: { title: 'menu_admin_company_structure', icon: 'tree' },
        component: () => import('@/layout/components/Sidebar/SidebarSubMenu.vue'),
        children: [...companyStructureRoutes]
      } as RouteConfig,
      {
        path: '/production',
        name: 'AdminProduction',
        meta: { title: 'menu_admin_production', icon: 'factory' },
        component: () => import('@/layout/components/Sidebar/SidebarSubMenu.vue'),
        children: [...productionRoutes]
      } as RouteConfig,
      {
        path: '/transportation',
        name: 'AdminTransport',
        meta: { title: 'menu_admin_transport', icon: 'truck' },
        component: () => import('@/layout/components/Sidebar/SidebarSubMenu.vue'),
        children: [...transportRoutes]
      } as RouteConfig,
      {
        path: '/invoicing',
        name: 'AdminInvoicing',
        meta: { title: 'menu_admin_invoicing', icon: 'euro-admin' },
        component: () => import('@/layout/components/Sidebar/SidebarSubMenu.vue'),
        children: [...invoicingRoutes]
      } as RouteConfig,
      {
        path: '/pricing',
        name: 'AdminPricing',
        meta: { title: 'menu_admin_pricing', icon: 'tag-solid' },
        component: () => import('@/layout/components/Sidebar/SidebarSubMenu.vue'),
        children: [...pricingRoutes]
      } as RouteConfig
    ],
    component: LazyLayout,
    meta: { icon: 'settings', title: 'menu_admin' },
    name: 'Management',
    path: '/management'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/fabrics/fabrics/index.vue'),
        meta: {
          permissions: [{ Fabric: PermissionLevel.Read }],
          title: 'menu_admin_fabrics',
          refreshOnDomainChange: true
        },
        name: 'Fabrics',
        path: 'fabrics'
      },
      {
        component: () => import('@/pages/fabrics/fabrics/create.vue'),
        hidden: true,
        meta: {
          icon: 'edit',
          permissions: [{ Fabric: PermissionLevel.Write }],
          title: 'createFabric',
          disableDomainChange: true
        },
        name: 'CreateFabric',
        path: 'fabrics/create'
      },
      {
        component: () => import('@/pages/fabrics/fabrics/edit.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ Fabric: PermissionLevel.Read }],
          title: 'editFabric',
          disableDomainChange: true
        },
        name: 'EditFabric',
        path: 'fabrics/edit/:id'
      },
      {
        component: () => import('@/pages/fabrics/orders/index.vue'),
        meta: {
          permissions: [{ FabricOrder: PermissionLevel.Read }],
          title: 'menu_admin_fabric_order',
          refreshOnDomainChange: true
        },
        name: 'FabricOrder',
        path: 'fabricorder'
      },
      {
        component: () => import('@/pages/fabrics/orders/create.vue'),
        hidden: true,
        meta: {
          icon: 'edit',
          permissions: [{ FabricOrder: PermissionLevel.Write }],
          title: 'createFabricOrder',
          disableDomainChange: true
        },
        name: 'CreateFabricOrder',
        path: 'fabricorder/create'
      },
      {
        component: () => import('@/pages/fabrics/orders/edit.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ FabricOrder: PermissionLevel.Read }],
          title: 'editFabricOrder',
          disableDomainChange: true
        },
        name: 'EditFabricOrder',
        path: 'fabricorder/edit/:id'
      },
      {
        component: () => import('@/pages/fabrics/orders/components/orderlines/EditFabricRolls.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ FabricOrder: PermissionLevel.Read }],
          title: 'editFabricRolls',
          disableDomainChange: true
        },
        name: 'EditFabricRolls',
        path: 'fabricorderline/edit/:id'
      },
      {
        component: () => import('@/pages/fabrics/stockcorrections/index.vue'),
        meta: {
          title: 'menu_fabric_stockcorrections',
          permissions: [{ StockCorrection: PermissionLevel.Write }],
          disableDomainChange: true
        },
        name: 'StockCorrection',
        path: 'stockcorrections'
      },
      {
        component: () => import('@/pages/fabrics/stockreport/index.vue'),
        meta: { title: 'menu_fabric_stockreport', permissions: [{ FabricStockReport: PermissionLevel.Read }] },
        name: 'FabricStockReport',
        path: 'fabricstockreport'
      },
      {
        component: () => import('@/pages/fabrics/inventory/Inventory.vue'),
        meta: {
          title: 'menu_fabric_inventory',
          permissions: [{ StockCorrection: PermissionLevel.Write }]
        },
        name: 'Inventory',
        path: 'inventory'
      },
      {
        component: () => import('@/pages/fabrics/fabricreport/FabricReport.vue'),
        meta: {
          permissions: [{ FabricReport: PermissionLevel.Read }],
          title: 'menu_fabric_report'
        },
        name: 'FabricReport',
        path: 'fabric-report'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'fabric', title: 'menu_fabric_management' },
    name: 'FabricManagement',
    path: '/fabric'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/masterdata/clients/index.vue'),
        meta: {
          permissions: [{ Client: PermissionLevel.Read }],
          title: 'menu_clients',
          refreshOnDomainChange: true
        },
        name: 'Clients',
        path: 'clients'
      },
      {
        component: () => import('@/pages/masterdata/clients/create.vue'),
        hidden: true,
        meta: {
          icon: 'edit',
          permissions: [{ Client: PermissionLevel.Write }],
          title: 'createClient'
        },
        name: 'CreateClient',
        path: 'clients/create'
      },
      {
        component: () => import('@/pages/masterdata/clients/edit.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ Client: PermissionLevel.Read }],
          title: 'editClient'
        },
        name: 'EditClient',
        path: 'clients/edit/:id'
      },
      {
        component: () => import('@/pages/masterdata/stores/create.vue'),
        hidden: true,
        meta: {
          icon: 'edit',
          // This permission must align with client, otherwise it causes
          // the Clients menu item to be visible incorrectly since the
          // Store Create and Edit pages are hidden.
          permissions: [{ Client: PermissionLevel.Write }],
          title: 'createStore',
          disableDomainChange: true
        },
        name: 'CreateStore',
        path: 'clients/edit/:clientID/domain/:domainID/stores/create'
      },
      {
        component: () => import('@/pages/masterdata/stores/edit.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ Client: PermissionLevel.Read }],
          title: 'editStore',
          disableDomainChange: true
        },
        name: 'EditStore',
        path: 'clients/edit/:clientID/domain/:domainID/stores/edit/:id'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'user', title: 'menu_client_management' },
    name: 'ClientManagement',
    path: '/client'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/orderlines/index.vue'),
        meta: {
          permissions: [{ Order: PermissionLevel.Read }],
          title: 'menu_orderlines',
          refreshOnDomainChange: true
        },
        name: 'OrderLines',
        path: 'orderlines'
      },
      {
        component: () => import('@/pages/orderlines/create.vue'),
        hidden: true,
        meta: {
          icon: 'edit',
          permissions: [{ Order: PermissionLevel.Write }],
          title: 'createOrderLine',
          disableDomainChange: true
        },
        name: 'CreateOrderLine',
        path: 'orderlines/create'
      },
      {
        component: () => import('@/pages/orderlines/edit.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ Order: PermissionLevel.Read }],
          title: 'editOrderLine',
          disableDomainChange: true
        },
        name: 'EditOrderLine',
        path: 'orderlines/edit/:id'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'shopping', title: 'menu_order_handling' },
    name: 'Order',
    path: '/order'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/orders/create.vue'),
        meta: {
          icon: 'edit',
          permissions: [{ Order: PermissionLevel.Write }],
          title: 'menu_show_orders',
          disableDomainChange: true
        },
        name: 'CreateOrder',
        path: ''
      }
    ],
    component: LazyLayout,
    meta: { icon: 'edit', title: 'menu_create_order' },
    path: '/order/create'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/orders/CreateMultiple/index.vue'),
        meta: {
          icon: 'edit',
          permissions: [{ InteriorDecoratorOrder: PermissionLevel.Write }],
          title: 'createOrder',
          disableDomainChange: true
        },
        name: 'CreateOrderDraft',
        path: 'create'
      },
      {
        component: () => import('@/pages/orders/CreateMultiple/index.vue'),
        meta: {
          icon: 'edit',
          permissions: [{ InteriorDecoratorOrder: PermissionLevel.Write }],
          title: 'editOrder',
          disableDomainChange: true
        },
        hidden: true,
        name: 'EditOrderDraft',
        path: 'edit/:id'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'edit', title: 'menu_create_interior_decorator_order' },
    path: '/order-draft'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/orderdrafts/index.vue'),
        meta: {
          permissions: [{ OrderDraft: PermissionLevel.Read }],
          title: 'menu_order_drafts'
        },
        name: 'OrderDrafts',
        path: ''
      }
    ],
    component: LazyLayout,
    meta: { icon: 'shopping', title: 'menu_order_drafts' },
    path: '/order-drafts'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/user/settings.vue'),
        meta: { noCache: true, title: 'userSettings' },
        name: 'Settings',
        path: 'settings'
      }
    ],
    component: LazyLayout,
    hidden: true,
    path: '/user',
    redirect: 'settings'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/planning/wizard/selectionWizard.vue'),
        meta: {
          noCache: true,
          permissions: [{ ProductionPlanning: PermissionLevel.Write }],
          title: 'menu_planning',
          disableRetailerChange: true
        },
        name: 'Wizard',
        path: '/wizard'
      },
      {
        component: () => import('@/pages/planning/series/index.vue'),
        meta: {
          permissions: [{ ProductionPlanning: PermissionLevel.Read }],
          title: 'menu_series',
          refreshOnDomainChange: true
        },
        name: 'Series',
        path: '/series'
      },
      {
        component: () => import('@/pages/planning/series/create.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ ProductionPlanning: PermissionLevel.Write }],
          title: 'createUrgentSeries'
        },
        name: 'CreateProductionPlanningSeries',
        path: '/series/create'
      },
      {
        component: () => import('@/pages/planning/series/edit.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ ProductionPlanning: PermissionLevel.Read }],
          title: 'editSeries'
        },
        name: 'EditSeries',
        path: 'series/edit/:id'
      },
      {
        component: () => import('@/pages/planning/series/ComponentList.vue'),
        meta: {
          permissions: [{ ProductionPlanning: PermissionLevel.Read }],
          title: 'menu_component_list'
        },
        name: 'ComponentList',
        path: '/componentList'
      },
      {
        component: () => import('@/pages/planning/series/StatusList.vue'),
        meta: {
          permissions: [{ ProductionPlanning: PermissionLevel.Read }],
          title: 'menu_status_list'
        },
        name: 'StatusList',
        path: 'statusList'
      },
      {
        component: () => import('@/pages/planning/orderpick/CreateOrderPickList.vue'),
        meta: {
          permissions: [{ OrderPickList: PermissionLevel.Read }],
          title: 'menu_order_pick_list'
        },
        name: 'CreateOrderPickList',
        path: 'createorderpicklist'
      },
      {
        component: () => import('@/pages/planning/orderpick/index.vue'),
        meta: {
          permissions: [{ OrderPickList: PermissionLevel.Read }],
          title: 'menu_order_pick_list_complete'
        },
        name: 'OrderPickLists',
        path: 'orderpicklists'
      },
      {
        component: () => import('@/pages/planning/orderpick/CompleteOrderPickList.vue'),
        hidden: true,
        meta: {
          permissions: [{ OrderPickList: PermissionLevel.Read }],
          title: 'editOrderPickList'
        },
        name: 'CompleteOrderPickList',
        path: 'orderpicklist/:id'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'schedule', title: 'menu_planning' },
    name: 'Planning',
    path: '/planning'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/transport/Planning.vue'),
        meta: {
          permissions: [{ TransportOrder: PermissionLevel.Write }],
          title: 'menu_transport_planning',
          disableDomainChange: true
        },
        name: 'TransportPlanning',
        path: 'planning'
      },
      {
        component: () => import('@/pages/transport/transportorders/index.vue'),
        meta: {
          permissions: [{ TransportOrder: PermissionLevel.Read }],
          title: 'menu_transport_list'
        },
        name: 'TransportOrders',
        path: 'transportorders'
      },
      {
        component: () => import('@/pages/transport/transportorders/edit.vue'),
        hidden: true,
        meta: {
          noCache: true,
          permissions: [{ TransportOrder: PermissionLevel.Read }],
          title: 'editTransportOrder',
          disableDomainChange: true
        },
        name: 'EditTransportOrder',
        path: 'transport/transportorders/edit/:id'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'truck', title: 'menu_transport' },
    name: 'Transport',
    path: '/transport'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/invoicing/ExternalInvoices.vue'),
        meta: {
          permissions: [{ ExternalInvoice: PermissionLevel.Read }],
          title: 'menu_external_invoices'
        },
        name: 'ExternalInvoices',
        path: 'external-invoices'
      },
      {
        component: () => import('@/pages/invoicing/InternalInvoices.vue'),
        meta: {
          permissions: [{ InternalInvoice: PermissionLevel.Read }],
          title: 'menu_internal_invoices'
        },
        name: 'InternalInvoices',
        path: 'internal-invoices'
      },
      {
        component: () => import('@/pages/invoicing/InvoiceSummary.vue'),
        meta: {
          permissions: [{ InvoiceSummary: PermissionLevel.Read }],
          title: 'menu_invoice_summary'
        },
        name: 'InvoiceSummary',
        path: 'invoice-summary'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'euro_sign', title: 'menu_invoicing' },
    name: 'Invoicing',
    path: '/invoicing'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/storeuserpages/fabrics/index.vue'),
        meta: {
          permissions: [{ StoreFabric: PermissionLevel.Read }],
          title: 'menu_store_fabrics'
        },
        name: 'CustomerServiceFabrics',
        path: ''
      }
    ],
    component: LazyLayout,
    meta: { icon: 'fabric', title: 'menu_store_fabrics' },
    path: '/Store/Fabrics'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/storeuserpages/orderlines/index.vue'),
        meta: {
          permissions: [{ StoreFabric: PermissionLevel.Read }],
          title: 'menu_store_orders'
        },
        name: 'CustomerServiceOrders',
        path: ''
      }
    ],
    component: LazyLayout,
    meta: { icon: 'form', title: 'menu_store_orders' },
    path: '/Store/OrderLines'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/storeuserpages/shipmentlist/index.vue'),
        meta: {
          permissions: [{ StoreFabric: PermissionLevel.Read }],
          title: 'menu_store_shipment_list'
        },
        name: 'CustomerServiceShipments',
        path: ''
      }
    ],
    component: LazyLayout,
    meta: { icon: 'truck', title: 'menu_store_shipment_list' },
    path: '/Store/ShipmentList'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/report/OrderReport.vue'),
        meta: {
          permissions: [{ OrderReport: PermissionLevel.Read }],
          title: 'menu_order_report'
        },
        name: 'OrderReport',
        path: 'orderReport'
      },
      {
        component: () => import('@/pages/report/OrderReportVolume.vue'),
        meta: {
          permissions: [{ OrderReportVolume: PermissionLevel.Read }],
          title: 'menu_order_report_volume'
        },
        name: 'OrderReportVolume',
        path: 'order-report-volume'
      },
      {
        component: () => import('@/pages/report/DeliveryReport.vue'),
        meta: {
          permissions: [{ DeliveryReport: PermissionLevel.Read }],
          title: 'menu_delivery_report'
        },
        name: 'DeliveryReport',
        path: 'delivery-report'
      },
      {
        component: () => import('@/pages/report/ClientSalesReport.vue'),
        meta: {
          permissions: [{ ClientSalesReport: PermissionLevel.Read }],
          title: 'menu_client_sales_report'
        },
        name: 'ClientSalesReport',
        path: 'client-sales-report'
      },
      {
        component: () => import('@/pages/report/OrderedMetersReport.vue'),
        meta: {
          permissions: [{ OrderedMetersReport: PermissionLevel.Read }],
          title: 'menu_ordered_meters_report'
        },
        name: 'OrderedMetersReport',
        path: 'ordered-meters-report'
      },
      {
        component: () => import('@/pages/report/ReturnReport.vue'),
        meta: {
          permissions: [{ ReturnReport: PermissionLevel.Read }],
          title: 'menu_return_report'
        },
        name: 'ReturnReport',
        path: 'return-report'
      },
      {
        component: () => import('@/pages/report/DeliveryTimeReport.vue'),
        meta: {
          permissions: [{ DeliveryTimeReport: PermissionLevel.Read }],
          title: 'menu_delivery_time_report'
        },
        name: 'DeliveryTimeReport',
        path: 'delivery-time-report'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'form', title: 'menu_reports' },
    name: 'Report',
    path: '/report'
  } as RouteConfig,
  {
    children: [
      {
        component: () => import('@/pages/hema/index.vue'),
        meta: {
          permissions: [{ HemaPendingProject: PermissionLevel.Read }],
          title: 'menu_hema'
        },
        name: 'PendingProjects',
        path: 'pending-projects'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'shopping', title: 'menu_order_handling' },
    name: 'Hema',
    path: '/hema'
  } as RouteConfig,
  {
    children: [
      // PLOP: Application routes
      {
        component: () => import('@/pages/kwantum/index.vue'),
        meta: {
          permissions: [{ QueuedKwantumOrder: PermissionLevel.Read }],
          title: 'menu_queued_kwantum_orders'
        },
        name: 'QueuedKwantumOrders',
        path: 'queued-kwantum-orders'
      }
    ],
    component: LazyLayout,
    meta: { icon: 'tree', title: 'menu_admin_masterdata' },
    name: 'Kwantum',
    path: '/kwantum'
  }
];
